import { Calendar, Check, X } from 'lucide-react'
import { useState } from 'react'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'

interface ApprovalCardProps {
  enrollmentId: string
  applicantName: string
  trainingTitle: string
  appliedAt?: string | null
  status: string
  onApprove: (id: string, comment?: string) => void
  onReject: (id: string, comment?: string) => void
  isApproving?: boolean
  isRejecting?: boolean
}

export function ApprovalCard({
  enrollmentId,
  applicantName,
  trainingTitle,
  appliedAt,
  status,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: ApprovalCardProps) {
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectComment, setRejectComment] = useState('')
  const isPending = status === 'pending'

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">{trainingTitle}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">申込者: {applicantName}</p>
            </div>
            <StatusBadge type="approval" status={status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {appliedAt && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>申込日: {new Date(appliedAt).toLocaleDateString('ja-JP')}</span>
            </div>
          )}

          {isPending && (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1"
                onClick={() => onApprove(enrollmentId)}
                disabled={isApproving || isRejecting}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                承認
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={() => setRejectOpen(true)}
                disabled={isApproving || isRejecting}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                却下
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>却下理由</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="却下理由を入力してください（任意）"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              キャンセル
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onReject(enrollmentId, rejectComment || undefined)
                setRejectOpen(false)
                setRejectComment('')
              }}
              disabled={isRejecting}
            >
              却下する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
